import { useNavigate, useLocation } from 'react-router-dom'
import { ScreenHeader } from '@/features/AddressSearchScreen/Components/ScreenHeader'
import { StoreList } from '@/components/StoreList'
import { InfoBox } from '@/components/ui/InfoBox'
import { useGetDeliveryStores } from './hooks/useGetDeliveryStores'
import { config } from '@/config/config'
import type { DeliveryAddress, Store } from '@/types'
import { useEffect } from 'react'
import { NoDeliveryAvailable } from './Components/NoDeliveryAvailable'
import { buildStoreRedirectUrl } from '@/utils/storeRedirect'

export function DeliveryResultsScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const deliveryAddress = location.state?.deliveryAddress as DeliveryAddress | undefined

  // Redirect back if no delivery address
  useEffect(() => {
    if (!deliveryAddress) {
      navigate('/address-search', { replace: true })
    }
  }, [deliveryAddress, navigate])

  // Fetch eligible delivery stores using the new API
  const { data: eligibleDeliveryStores = [], isLoading: isLoadingDeliveryStores } = useGetDeliveryStores({
    restaurantId: config.store.restaurantId,
    destinationLat: deliveryAddress?.latitude || 0,
    destinationLong: deliveryAddress?.longitude || 0,
    sub1: deliveryAddress?.postcode || '',
    sub2: deliveryAddress?.state || '',
    enabled: !!deliveryAddress,
  })

  const handleSwitchToPickup = () => {
    navigate('/')
  }

  const handleChangeAddress = () => {
    navigate('/address-search')
  }

  const handleGoBack = () => {
    navigate('/')
  }

  const handleDeliveryStoreSelect = (store: Store) => {
    if (!deliveryAddress) return

    const url = buildStoreRedirectUrl({
      store: store as Store & {
        minOrderValue?: string
        deliveryCost?: string
        suburbId?: string
      },
      fulfillmentMode: 'delivery',
      deliveryAddress: {
        street: deliveryAddress.street,
        suburb: deliveryAddress.suburb,
        postcode: deliveryAddress.postcode,
        state: deliveryAddress.state,
        latitude: deliveryAddress.latitude,
        longitude: deliveryAddress.longitude,
        formatted: deliveryAddress.formatted,
        placeId: deliveryAddress.placeId,
        areaCode: deliveryAddress.areaCode,
        area: deliveryAddress.area,
      },
    })

    if (url) {
      window.open(url, '_blank')
    }
  }

  return (
    <div className="min-h-dvh flex flex-col bg-surface-secondary">
      <ScreenHeader
        title="Delivering to"
        subtitle={deliveryAddress?.formatted || 'Unknown address'}
        showBack
        onBack={handleGoBack}
        rightAction={
          <button
            onClick={handleChangeAddress}
            className="text-sm/5 font-medium text-(--color-brand-primary)"
          >
            Change
          </button>
        }
      />

      <main className="flex-1 px-4 py-4">
        {isLoadingDeliveryStores ? (
          <div className="py-12 text-center">
            <div className="inline-block size-8 border-2 border-(--color-brand-primary) border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-text-secondary">
              Checking delivery availability...
            </p>
          </div>
        ) : eligibleDeliveryStores.length > 0 ? (
          <>
            <p className="text-sm/5 text-text-secondary mb-4">
              {eligibleDeliveryStores.length} store{eligibleDeliveryStores.length !== 1 ? 's' : ''} can deliver to your address
            </p>

            <StoreList
              stores={eligibleDeliveryStores}
              variant="delivery"
              onSelect={handleDeliveryStoreSelect}
            />

            {/* Delivery info */}
            {eligibleDeliveryStores.length > 0 && (
              <div className="mt-4">
                <InfoBox
                  variant="success"
                  message="Tap a store to order for delivery. Delivery fees and minimum orders vary by location."
                />
              </div>
            )}
          </>
        ) : (
          <NoDeliveryAvailable
            onSwitchToPickup={handleSwitchToPickup}
            onChangeAddress={handleChangeAddress}
          />
        )}
      </main>
    </div>
  )
}
